import React from 'react';
import {
  ApplicationAlert,
  AlertFilters,
  AlertType,
  PaginationData,
} from '../../types/admin';
import { getAlertLabel } from './alertCatalog';
import AlertStatusBadge from './AlertStatusBadge';
import AlertPriorityBadge from './AlertPriorityBadge';
import AlertActionsMenu from './AlertActionsMenu';
import AlertsFilters from './AlertsFilters';
import Pagination from 'shared/components/Pagination';
import styles from './ApplicationListTable.module.css';

interface ApplicationAlertsTableProps {
  alerts: ApplicationAlert[];
  isLoading?: boolean;
  pagination?: PaginationData;
  onPageChange?: (page: number) => void;
  filters?: AlertFilters;
  onFilterChange?: (key: keyof AlertFilters, value: string) => void;
  onTypesChange?: (types: AlertType[]) => void;
  onClearFilters?: () => void;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  onResolveAlert?: (alertId: string, resolutionNote: string) => void;
  onDismissAlert?: (alertId: string, note: string) => void;
  onFolioClick?: (folio: string) => void;
  showHeader?: boolean;
  typeCounts?: Partial<Record<AlertType, number>>;
}

const ApplicationAlertsTable: React.FC<ApplicationAlertsTableProps> = ({
  alerts,
  isLoading = false,
  pagination,
  onPageChange,
  filters = {},
  onFilterChange,
  onTypesChange,
  onClearFilters,
  searchTerm = '',
  onSearchChange,
  onResolveAlert,
  onDismissAlert,
  onFolioClick,
  showHeader = true,
  typeCounts,
}) => {
  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleSort = (col: string) => {
    if (!onFilterChange) return;
    if (filters.sortBy === col) {
      onFilterChange('sortDir', filters.sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onFilterChange('sortBy', col);
      onFilterChange('sortDir', 'asc');
    }
  };

  const SortIcon: React.FC<{ col: string }> = ({ col }) => {
    const active = filters.sortBy === col;
    const icon = active
      ? (filters.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward')
      : 'unfold_more';
    return (
      <i className={`uc-icon icon-size--sm ${active ? styles.sortIconActive : styles.sortIcon}`}>
        {icon}
      </i>
    );
  };

  const th = (label: string, col: string) => (
    <th className={`${styles.sortableHeader}`} onClick={() => handleSort(col)}>
      <span className={styles.sortHeaderContent}>
        {label}<SortIcon col={col} />
      </span>
    </th>
  );

  if (isLoading) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Alertas de Postulaciones</h3>
        </div>
        <div className={styles.loading}>Cargando alertas...</div>
      </div>
    );
  }

  return (
    <div>
      {showHeader && (
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderText}>
            <h3>Alertas de Postulaciones</h3>
            <p className={styles.count}>Alertas del sistema que requieren atención administrativa</p>
          </div>
        </div>
      )}

      {onFilterChange && onSearchChange && onTypesChange && (
        <AlertsFilters
          filters={filters}
          onFilterChange={onFilterChange}
          onTypesChange={onTypesChange}
          onClearFilters={onClearFilters}
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          typeCounts={typeCounts}
        />
      )}

      <div className={styles.statusGroup}>
        {alerts.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No hay alertas que coincidan con los filtros aplicados.</p>
          </div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {th('Tipo de Alerta', 'alertType')}
                    {th('Origen', 'origin')}
                    {th('Título', 'title')}
                    {th('Prioridad', 'priority')}
                    {th('Estado', 'status')}
                    {th('Fecha', 'createdAt')}
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.id} className={styles.row}>
                      <td>
                        <span className={styles.alertTypeCell}>{getAlertLabel(alert.alertType)}</span>
                      </td>
                      <td>
                        {alert.application?.applicationNumber && (
                          <div>
                            <button
                              onClick={() => window.open(`/admin/application/${alert.application!.id}`, '_blank')}
                              className={styles.folioLink}
                              type="button"
                            >
                              {alert.application.applicationNumber}
                            </button>
                          </div>
                        )}
                        {alert.evaluator && (
                          <div className={styles.evaluatorName}>
                            <span className={styles.evaluatorLabel}>Evaluador:</span>{' '}
                            {alert.evaluator.name}
                          </div>
                        )}
                        {!alert.application?.applicationNumber && !alert.evaluator && (
                          <span>—</span>
                        )}
                      </td>
                      <td>
                        <div>
                          <div className={styles.alertTitle}>{alert.title}</div>
                          <div className={styles.applicantEmail}>{alert.description}</div>
                        </div>
                      </td>
                      <td>
                        <AlertPriorityBadge priority={alert.priority} />
                      </td>
                      <td>
                        <AlertStatusBadge status={alert.status} />
                      </td>
                      <td>
                        <span className={styles.date}>{formatDate(alert.createdAt)}</span>
                      </td>
                      <td>
                        {onResolveAlert && onDismissAlert && (
                          <AlertActionsMenu
                            alert={alert}
                            onResolve={onResolveAlert}
                            onDismiss={onDismissAlert}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && onPageChange && pagination.totalPages > 1 && (
              <div>
                <Pagination pagination={pagination} onPageChange={onPageChange} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationAlertsTable;
